import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, X, FileText, ArrowRight } from 'lucide-react';

export const Gallery = ({ images, alt, index, setIndex }) => {
  const [open, setOpen] = useState(false);
  const step = (d) => setIndex((i) => (i + d + images.length) % images.length);
  if (!images.length) return <div className="aspect-square bg-gray-50 rounded-lg" />;
  return (
    <div data-testid="product-gallery">
      <button type="button" onClick={() => setOpen(true)} className="block w-full aspect-square bg-gray-50 rounded-lg overflow-hidden mb-3 cursor-zoom-in" data-testid="main-product-image">
        <img src={images[index]} alt={alt} className="w-full h-full object-contain mix-blend-multiply" />
      </button>
      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2" data-testid="thumbnail-strip">
          {images.map((img, i) => (
            <button key={img} type="button" onClick={() => setIndex(i)} data-testid={`thumbnail-${i}`}
              className={`shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-colors ${i === index ? 'border-gray-900' : 'border-gray-200 hover:border-gray-400'}`}>
              <img src={img} alt="" className="w-full h-full object-contain mix-blend-multiply bg-gray-50" />
            </button>
          ))}
        </div>
      )}
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center" onClick={() => setOpen(false)} data-testid="lightbox">
          <button className="absolute top-4 right-4 text-white/70 hover:text-white" data-testid="lightbox-close"><X className="h-8 w-8" /></button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); step(-1); }} className="absolute left-2 text-white/70 hover:text-white p-2" data-testid="lightbox-prev"><ChevronLeft className="h-10 w-10" /></button>
              <button onClick={(e) => { e.stopPropagation(); step(1); }} className="absolute right-2 text-white/70 hover:text-white p-2" data-testid="lightbox-next"><ChevronRight className="h-10 w-10" /></button>
            </>
          )}
          <img src={images[index]} alt={alt} className="max-w-[90vw] max-h-[90vh] object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export const VariantSelector = ({ variants, selected, onSelect }) => (
  <div className="mb-8" data-testid="variant-selector">
    <p className="text-xs uppercase tracking-widest text-gray-400 mb-3">{variants.length} variants · finish / colour</p>
    <div className="flex flex-col gap-2 max-h-80 overflow-y-auto pr-1">
      {variants.map((v, i) => (
        <button key={v.code} type="button" onClick={() => onSelect(i)} data-testid={`variant-option-${v.code}`}
          className={`flex items-center gap-3 text-left p-2 rounded-md border transition-colors ${i === selected ? 'border-gray-900 bg-gray-50' : 'border-gray-200 hover:border-gray-400'}`}>
          {v.image && <img src={v.image} alt="" className="w-10 h-10 object-contain bg-white rounded shrink-0" />}
          <span className="min-w-0">
            <span className="block text-sm text-gray-900 truncate">{[v.label, v.finish].filter(Boolean).join(' · ') || v.code}</span>
            <span className="block text-xs font-mono text-gray-500">{v.code}{v.dimensions ? ` · ${v.dimensions}` : ''}</span>
          </span>
        </button>
      ))}
    </div>
  </div>
);

export const SpecTable = ({ variant }) => {
  const rows = [
    ['Art. no.', variant.code], ['Finish / colour', variant.finish], ['Dimensions', variant.dimensions],
    ['Light source', variant.lightSource], ['Protection class', variant.protectionClass],
    ['Mounting', variant.mounting], ['Cable / canopy', variant.connection],
  ].filter(([, v]) => v);
  return (
    <section className="bg-gray-50 rounded-lg p-6 sm:p-8" data-testid="specs-section">
      <h2 className="text-xl font-medium text-gray-900 mb-4">Technical specification</h2>
      <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between gap-6 py-3 border-b border-gray-200">
            <dt className="text-sm font-medium text-gray-500 shrink-0">{k}</dt>
            <dd className="text-sm text-gray-900 text-right" data-testid={`spec-${k.toLowerCase().replace(/[^a-z]+/g, '-')}`}>{v}</dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-gray-500 mt-4">Lamp to be specified to suit the socket shown, unless stated as included.</p>
    </section>
  );
};

export const DocumentList = ({ documents }) => !documents?.length ? null : (
  <section data-testid="documents-section">
    <h2 className="text-xl font-medium text-gray-900 mb-4">Downloads</h2>
    <ul className="space-y-2">
      {documents.map((d) => (
        <li key={d.url}>
          <a href={d.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-sm text-gray-700 hover:text-gray-900 hover:underline" data-testid="document-link">
            <FileText className="h-4 w-4 mr-2 text-gray-400" />{d.name}
          </a>
        </li>
      ))}
    </ul>
  </section>
);

export const FamilyStrip = ({ brandId, family, familySlug, products }) => !products?.length ? null : (
  <section className="mt-16" data-testid="family-section">
    <div className="flex items-end justify-between mb-6 gap-4">
      <h2 className="text-2xl font-light text-gray-900">Also available in this family</h2>
      <Link to={`/brand/${brandId}/families/${familySlug}`} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 shrink-0" data-testid="family-overview-link">
        All {family} <ArrowRight className="h-4 w-4 ml-1" />
      </Link>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
      {products.map((p) => (
        <Link key={p.slug} to={`/brand/${brandId}/products/${p.slug}`} className="group" data-testid={`family-product-${p.slug}`}>
          <div className="aspect-square bg-gray-50 rounded-lg overflow-hidden mb-2">
            {p.mainImage && <img src={p.mainImage} alt={p.title} className="w-full h-full object-contain mix-blend-multiply transition-transform duration-500 group-hover:scale-105" loading="lazy" />}
          </div>
          <p className="text-sm text-gray-900 truncate">{p.name}</p>
          <p className="text-xs text-gray-500">{p.productType} · {p.code}</p>
        </Link>
      ))}
    </div>
  </section>
);
